import React from "react";

import { TFunction, formatNumber, trimToDecimals } from "utils";

import { FOOTPRINTING_CATEGORY_COLORS } from "consts";

import {
  CropFootprintCalculationSummary,
  CropResultsSubDataRowSource,
  DairyFootprintCalculationSummary,
  DairyResultsSubDataRowSource,
  FootprintingCropEmissionsSourceCategory,
  FootprintingCropResultsDataTableRow,
  FootprintingDairyEmissionsSourceCategory,
  FootprintingDairyResultsDataTableRow,
} from "models";

import { Flex, LazyLoadImage, Svg, Typography } from "components";

/**
 * Note: PDF Styles have to be manually managed since css doesn't work the same
 * way in the PDF rendering engine
 */
const cellPad = "2px 8px";
const cellStyle: React.CSSProperties = {
  padding: cellPad,
  textAlign: "right",
  wordBreak: "break-word",
  fontSize: 7.5,
};
const headerStyle: React.CSSProperties = {
  ...cellStyle,
  borderBottom: "1px solid #e8e8e8",
  fontWeight: "bold",
  color: "#6d6d6d",
};

const CROP_SCOPE_MAPPING: Record<FootprintingCropEmissionsSourceCategory, Partial<Record<CropResultsSubDataRowSource, number>> | number> = {
  energy: { diesel: 1, benzin: 1, naturalGas: 1, heating: 1, electricity: 2 },
  fertilizer: { mineralFertilizers: 3, organicFertilizers: 3, lime: 3 },
  fieldWork: { harvesting: 1, fertilizerApplication: 1, plantProtectionApplications: 1, seedingApplication: 1, tillageApplication: 1 },
  transportation: { upstreamTransportation: 3, downstreamTransportation: 3 },
  other: { water: 3 },
  plantProtection: 1,
  luc: 1,
  residue: 1,
  waste: 3,
};

const DAIRY_SCOPE_MAPPING: Record<FootprintingDairyEmissionsSourceCategory, Partial<Record<DairyResultsSubDataRowSource, number>> | number> = {
  stockRenewal: 1,
  calfRearing: 1,
  feeding: 1,
  manure: 1,
  water: 1,
  energy: 1,
  machinery: 1,
  luc: 1,
  refrigerants: 1,
  waste: 1,
  grasslandFertiliser: 1,
};

const getDairyCategoryScope = (t: TFunction, category: FootprintingDairyEmissionsSourceCategory, row: FootprintingDairyResultsDataTableRow): string => {
  const categoryScope = DAIRY_SCOPE_MAPPING[category];
  if (typeof categoryScope === "number") {
    return t("resultsPage.table.rowValue.scope", { scope: categoryScope });
  }
  const allSameScope = row.subData.length > 0 && new Set(row.subData.map((sd) => categoryScope[sd.source])).size === 1;
  if (allSameScope) {
    return t("resultsPage.table.rowValue.scope", { scope: categoryScope[row.subData[0].source] });
  }
  return t("resultsPage.table.rowValue.mixed");
};

const getCategoryScope = (t: TFunction, category: FootprintingCropEmissionsSourceCategory, row: FootprintingCropResultsDataTableRow): string => {
  const categoryScope = CROP_SCOPE_MAPPING[category];
  if (typeof categoryScope === "number") {
    return t("resultsPage.table.rowValue.scope", { scope: categoryScope });
  }
  const allSameScope = row.subData.length > 0 && new Set(row.subData.map((sd) => categoryScope[sd.source])).size === 1;
  if (allSameScope) {
    return t("resultsPage.table.rowValue.scope", { scope: categoryScope[row.subData[0].source] });
  }
  return t("resultsPage.table.rowValue.mixed");
};

const renderCategoryBreakdown = (t: TFunction, summary: CropFootprintCalculationSummary, hidePerYield = false) => {
  const entries = (Object.entries(summary.tableData) as [FootprintingCropEmissionsSourceCategory, FootprintingCropResultsDataTableRow][]).sort(
    (a, b) => b[1].data.totalCO2e - a[1].data.totalCO2e
  );

  return (
    <div className="footprinting-pdf-export-table-wrap" style={{ marginTop: 12 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <thead>
          <tr>
            <th style={{ ...headerStyle, textAlign: "left", width: hidePerYield ? "24%" : "21%" }}>{t("resultsPage.table.columnHeader.source")}</th>
            <th style={{ ...headerStyle, width: hidePerYield ? "12%" : "10%" }}>{t("resultsPage.table.columnHeader.scope")}</th>
            <th style={{ ...headerStyle, width: hidePerYield ? "20%" : "16%" }}>{t("resultsPage.table.columnHeader.totalCO2e")}</th>
            <th style={{ ...headerStyle, width: hidePerYield ? "20%" : "16%" }}>{t("resultsPage.table.columnHeader.perHaCO2e")}</th>
            {!hidePerYield && <th style={{ ...headerStyle, width: "16%" }}>{t("resultsPage.table.columnHeader.perKgYieldCO2e")}</th>}
            <th style={{ ...headerStyle, width: hidePerYield ? "24%" : "21%", whiteSpace: "nowrap" }}>{t("resultsPage.table.columnHeader.shareOfTotal")}</th>
          </tr>
        </thead>
        <tbody>
          {entries.flatMap(([category, row]) => {
            const categoryScope = CROP_SCOPE_MAPPING[category];
            const hasSubCategories = typeof categoryScope === "object" && Object.keys(categoryScope).length > 0;

            const categoryRow = (
              <tr key={category} style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ ...cellStyle, textAlign: "left", fontWeight: "bold" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: FOOTPRINTING_CATEGORY_COLORS[category],
                      marginRight: 6,
                      verticalAlign: "middle",
                    }}
                  />
                  {t(`categories.${category}`)}
                </td>
                <td style={cellStyle}>{getCategoryScope(t, category, row)}</td>
                <td style={cellStyle}>{formatNumber({ number: trimToDecimals(row.data.totalCO2e, 2) })}</td>
                <td style={cellStyle}>{formatNumber({ number: trimToDecimals(row.data.perHaCO2e, 2) })}</td>
                {!hidePerYield && <td style={cellStyle}>{formatNumber({ number: trimToDecimals(row.data.perKgYieldCO2e, 2) })}</td>}
                <td style={cellStyle}>{formatNumber({ number: trimToDecimals(100 * row.data.shareOfTotal, 2) })}</td>
              </tr>
            );

            const sortedSubData = hasSubCategories ? [...row.subData].sort((a, b) => b.totalCO2e - a.totalCO2e) : [];

            const subRows = hasSubCategories
              ? sortedSubData.map((sub) => {
                  const scopeMap = categoryScope as Partial<Record<string, number>>;
                  const subScope = scopeMap[sub.source];
                  return (
                    <tr key={`${category}.${sub.source}`} style={{ borderBottom: "1px solid #f5f5f5" }}>
                      <td style={{ ...cellStyle, textAlign: "left", paddingLeft: 20 }}>{t(`subCategories.${sub.source}`)}</td>
                      <td style={cellStyle}>{subScope ? t("resultsPage.table.rowValue.scope", { scope: subScope }) : ""}</td>
                      <td style={cellStyle}>{formatNumber({ number: trimToDecimals(sub.totalCO2e, 2) })}</td>
                      <td style={cellStyle}>{formatNumber({ number: trimToDecimals(sub.perHaCO2e, 2) })}</td>
                      {!hidePerYield && <td style={cellStyle}>{formatNumber({ number: trimToDecimals(sub.perKgYieldCO2e, 2) })}</td>}
                      <td style={cellStyle}>{formatNumber({ number: trimToDecimals(sub.shareOfTotal, 2) })}</td>
                    </tr>
                  );
                })
              : [];

            return [categoryRow, ...subRows];
          })}
        </tbody>
      </table>
    </div>
  );
};

const renderDairyCategoryBreakdown = (t: TFunction, summary: DairyFootprintCalculationSummary) => {
  const entries = (Object.entries(summary.tableData) as [FootprintingDairyEmissionsSourceCategory, FootprintingDairyResultsDataTableRow][]).sort(
    (a, b) => b[1].data.totalCO2e - a[1].data.totalCO2e
  );

  return (
    <div className="footprinting-pdf-export-table-wrap" style={{ marginTop: 12 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <thead>
          <tr>
            <th style={{ ...headerStyle, textAlign: "left", width: "28%" }}>{t("resultsPage.table.columnHeader.source")}</th>
            <th style={{ ...headerStyle, width: "12%" }}>{t("resultsPage.table.columnHeader.scope")}</th>
            <th style={{ ...headerStyle, width: "18%" }}>{t("resultsPage.table.columnHeader.totalCO2e")}</th>
            <th style={{ ...headerStyle, width: "18%" }}>{t("resultsPage.table.columnHeader.perKgYieldCO2e")}</th>
            <th style={{ ...headerStyle, width: "14%" }}>{t("resultsPage.table.columnHeader.perCowCO2e")}</th>
            <th style={{ ...headerStyle, width: "10%" }}>{t("resultsPage.table.columnHeader.shareOfTotal")}</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([category, row]) => (
            <tr key={category} style={{ borderBottom: "1px solid #eee" }}>
              <td style={{ ...cellStyle, textAlign: "left", fontWeight: "bold" }}>
                <span
                  style={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: FOOTPRINTING_CATEGORY_COLORS[category],
                    marginRight: 6,
                    verticalAlign: "middle",
                  }}
                />
                {t(`categories.${category}`)}
              </td>
              <td style={cellStyle}>{getDairyCategoryScope(t, category, row)}</td>
              <td style={cellStyle}>{formatNumber({ number: trimToDecimals(row.data.totalCO2e, 2) })}</td>
              <td style={cellStyle}>{formatNumber({ number: trimToDecimals(row.data.perKgYieldCO2e, 2) })}</td>
              <td style={cellStyle}>{formatNumber({ number: trimToDecimals(row.data.perCowCO2e, 2) })}</td>
              <td style={cellStyle}>{formatNumber({ number: trimToDecimals(100 * row.data.shareOfTotal, 2) })}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Shared sub-components ───────────────────────────────────────────────────

interface PageHeaderProps {
  farmName: string;
  subtitle: string;
  title: string;
  year: number;
}

const PageHeader = ({ farmName, subtitle, title, year }: PageHeaderProps) => (
  <Flex className="footprinting-pdf-page-header" justify="space-between" align="center">
    <div className="footprinting-pdf-page-header-breadcrumb">
      {title} {farmName} | {subtitle} {year}
    </div>
    <Svg svgName="icons/klim/brandmark-with-text" width={64} height={18} />
  </Flex>
);

interface PageFooterProps {
  t: TFunction;
  infoText?: string;
  currentPage?: number;
  totalPages?: number;
}

const PageFooter = ({ t, infoText, currentPage, totalPages }: PageFooterProps) => (
  <Flex className="footprinting-pdf-page-footer-row" justify="space-between" align="center">
    <div className="footprinting-pdf-page-footer-info">{infoText ?? ""}</div>
    {typeof currentPage === "number" && typeof totalPages === "number" && (
      <div className="footprinting-pdf-page-footer-page">
        {t("pdfExport.pageOf", { current: currentPage, total: totalPages, defaultValue: "Seite {{current}} von {{total}}" })}
      </div>
    )}
  </Flex>
);

// ── Cover page ──────────────────────────────────────────────────────────────

interface PDFExportCoverPageProps {
  t: TFunction;
  year: number;
  farmName: string;
  subtitle: string;
  title: string;
  date: string;
  coverPhotoSrc: string;
}

const CoverPage = ({ t, year, farmName, subtitle, title, date, coverPhotoSrc }: PDFExportCoverPageProps) => (
  <Flex className="footprinting-pdf-cover-page" vertical>
    {/* Base64 data URI avoids Firefox on macOS re-fetching the URL during PDF
        generation — external URLs are dropped from the saved file even when
        they appear correctly in the print preview. */}
    <LazyLoadImage
      alt=""
      src={coverPhotoSrc}
      wrapperClassName="footprinting-pdf-cover-photo"
      className="footprinting-pdf-cover-photo-img"
      delay={0}
      visibleByDefault
    />

    {/* Text overlaid on the photo */}
    <Flex className="footprinting-pdf-cover-text" vertical>
      <Typography variant="h1" className="footprinting-pdf-cover-title">
        {title}
      </Typography>
      <Typography variant="h3" className="footprinting-pdf-cover-subtitle">
        {t("pdfExport.reportYear")} {year}
      </Typography>
      <Flex className="footprinting-pdf-cover-info" vertical>
        <div>{farmName}</div>
        <div>
          {subtitle} {year}
        </div>
        <div>{t("pdfExport.klimGmbH", { defaultValue: "Klim GmbH" })}</div>
        <div>{date}</div>
      </Flex>
    </Flex>

    {/* Klim branding on the yellow area at the bottom */}
    <div className="footprinting-pdf-cover-footer">
      <LazyLoadImage alt="Klim" src="/images/footprinting/klim-wordmark.png" height={70} delay={0} visibleByDefault />
    </div>
  </Flex>
);

// ── Farm summary page ───────────────────────────────────────────────────────

interface BasePDFExportProps {
  t: TFunction;
  year: number;
  farmName: string;
  subtitle: string;
  title: string;
  regionalComparisonValue?: number | null;
}

interface PDFExportFarmPageProps extends BasePDFExportProps {
  cropList: Array<{
    cropId: string;
    cropName: string;
    totalHectares: number;
    totalCO2e: number;
    perHaCO2e: number;
  }>;
  farmSummary: {
    summary: CropFootprintCalculationSummary;
    totalHectares: number;
    commodityCount: number;
  } | null;
  currentPage: number;
  totalPages: number;
}

const FarmPage = ({ t, title, subtitle, year, farmSummary, farmName, cropList, currentPage, totalPages }: PDFExportFarmPageProps) => (
  <Flex className="footprinting-pdf-export-overview-page" vertical>
    <PageHeader title={title} farmName={farmName} subtitle={subtitle} year={year} />

    <Typography variant="h2" className="footprinting-pdf-export-page-title">
      {t("pdfExport.co2BalanceYear", { year, defaultValue: "CO₂-Bilanz {{year}}" })}
    </Typography>

    {farmSummary && (
      <Flex className="footprinting-pdf-export-overview-content" vertical>
        <Typography variant="h2" className="footprinting-pdf-export-section-title">
          {t("pdfExport.farmOverview")}
        </Typography>

        <Flex gap={16} wrap="wrap" style={{ marginBottom: "1.25rem" }}>
          <Flex className="footprinting-pdf-export-overview-card" vertical>
            <div className="footprinting-pdf-export-overview-card-value">{formatNumber({ number: trimToDecimals(farmSummary.summary.overall.totalCO2e) })}</div>
            <div className="footprinting-pdf-export-overview-card-label">{t("resultsPage.summaryTiles.co2eInTotal")} (kg)</div>
          </Flex>
          <Flex className="footprinting-pdf-export-overview-card" vertical>
            <div className="footprinting-pdf-export-overview-card-value">
              {formatNumber({ number: trimToDecimals(farmSummary.summary.overall.perHaCO2e, 2) })}
            </div>
            <div className="footprinting-pdf-export-overview-card-label">{t("resultsPage.summaryTiles.co2ePerHa")} (kg)</div>
          </Flex>
          <Flex className="footprinting-pdf-export-overview-card" vertical>
            <div className="footprinting-pdf-export-overview-card-value">{farmSummary.commodityCount}</div>
            <div className="footprinting-pdf-export-overview-card-label">{t("pdfExport.commodities")}</div>
          </Flex>
        </Flex>

        <div className="footprinting-pdf-export-commodities-wrap">
          <div className="footprinting-pdf-export-table-wrap">
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th style={{ ...headerStyle, textAlign: "left", width: "25%" }}>{t("pdfExport.commodity")}</th>
                  <th style={{ ...headerStyle, width: "20%", textAlign: "right" }}>{t("pdfExport.area")} (ha)</th>
                  <th style={{ ...headerStyle, width: "30%", textAlign: "right" }}>{t("resultsPage.table.columnHeader.totalCO2e")}</th>
                  <th style={{ ...headerStyle, width: "25%", textAlign: "right" }}>{t("resultsPage.table.columnHeader.perHaCO2e")}</th>
                </tr>
              </thead>
              <tbody>
                {cropList.map((item) => (
                  <tr key={item.cropId} style={{ borderBottom: "1px solid #eee" }}>
                    <td style={{ ...cellStyle, textAlign: "left" }}>{item.cropName}</td>
                    <td style={cellStyle}>{formatNumber({ number: trimToDecimals(item.totalHectares, 2) })}</td>
                    <td style={cellStyle}>{formatNumber({ number: trimToDecimals(item.totalCO2e) })}</td>
                    <td style={cellStyle}>{formatNumber({ number: trimToDecimals(item.perHaCO2e, 2) })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <Typography variant="h4" className="footprinting-pdf-export-breakdown-title" style={{ marginTop: 16 }}>
          {t("resultsPage.detailedBreakdownTitle")}
        </Typography>
        {renderCategoryBreakdown(t, farmSummary.summary, true)}
      </Flex>
    )}

    <PageFooter t={t} currentPage={currentPage} totalPages={totalPages} />
  </Flex>
);

// ── Crop page ───────────────────────────────────────────────────────────────

interface PDFExportCropPageProps extends BasePDFExportProps {
  cropName: string;
  totalHectares: number;
  totalYieldTonnes: number;
  normalizedYieldTonnes: number | null;
  calculationSummary: CropFootprintCalculationSummary;
  currentPage: number;
  totalPages: number;
}

const CropPage = ({
  t,
  title,
  subtitle,
  farmName,
  cropName,
  regionalComparisonValue,
  year,
  calculationSummary,
  totalHectares,
  totalYieldTonnes,
  normalizedYieldTonnes,
  currentPage,
  totalPages,
}: PDFExportCropPageProps) => {
  const regionalValueComparisonChange = regionalComparisonValue
    ? ((calculationSummary.overall.perKgYieldCO2e - regionalComparisonValue) / regionalComparisonValue) * 100
    : undefined;

  const footerInfo = [
    `${t("pdfExport.area")}: ${formatNumber({ number: trimToDecimals(totalHectares, 2) })} ha`,
    `${t("pdfExport.yield")}: ${formatNumber({ number: trimToDecimals(totalYieldTonnes, 2) })} t`,
    `${t("pdfExport.normalizedYield")}: ${
      typeof normalizedYieldTonnes === "number" ? `${formatNumber({ number: trimToDecimals(normalizedYieldTonnes, 2) })} t` : "N/A"
    }`,
  ].join(" | ");

  return (
    <Flex className="break-page footprinting-pdf-export-crop-page" vertical>
      <PageHeader title={title} farmName={farmName} subtitle={subtitle} year={year} />

      <Typography variant="h2" className="footprinting-pdf-export-crop-title">
        {cropName}
      </Typography>

      <Flex className="footprinting-pdf-export-crop-kpi-row" gap={12} wrap="wrap">
        <Flex className="footprinting-pdf-export-kpi-card footprinting-pdf-export-kpi-card-primary" vertical>
          <div className="footprinting-pdf-export-kpi-value footprinting-pdf-export-kpi-value-primary">
            {formatNumber({ number: trimToDecimals(calculationSummary.overall.totalCO2e) })}
          </div>
          <div className="footprinting-pdf-export-kpi-label footprinting-pdf-export-kpi-label-primary">{t("resultsPage.summaryTiles.co2eInTotal")} (kg)</div>
        </Flex>
        <Flex className="footprinting-pdf-export-kpi-card footprinting-pdf-export-kpi-card-neutral" vertical>
          <div className="footprinting-pdf-export-kpi-value footprinting-pdf-export-kpi-value-green">
            {formatNumber({ number: trimToDecimals(calculationSummary.overall.perKgYieldCO2e) })}
          </div>
          <div className="footprinting-pdf-export-kpi-label">{t("resultsPage.summaryTiles.co2ePerKgYield")} (kg)</div>
        </Flex>
        {regionalValueComparisonChange != null ? (
          <Flex className="footprinting-pdf-export-kpi-card footprinting-pdf-export-kpi-card-neutral" vertical>
            <div className="footprinting-pdf-export-kpi-value footprinting-pdf-export-kpi-value-green">
              {formatNumber({ number: trimToDecimals(regionalValueComparisonChange) })}%
            </div>
            <div className="footprinting-pdf-export-kpi-label">
              {regionalComparisonValue} {t("resultsPage.table.columnHeader.perKgYieldCO2e")}
            </div>
          </Flex>
        ) : null}
        <Flex className="footprinting-pdf-export-kpi-card footprinting-pdf-export-kpi-card-neutral" vertical>
          <div className="footprinting-pdf-export-kpi-value footprinting-pdf-export-kpi-value-green">
            {formatNumber({ number: trimToDecimals(calculationSummary.overall.perHaCO2e) })}
          </div>
          <div className="footprinting-pdf-export-kpi-label">{t("resultsPage.summaryTiles.co2ePerHa")} (kg)</div>
        </Flex>
      </Flex>

      <Typography variant="h4" className="footprinting-pdf-export-breakdown-title">
        {t("resultsPage.detailedBreakdownTitle")}
      </Typography>
      {renderCategoryBreakdown(t, calculationSummary)}

      <PageFooter t={t} infoText={footerInfo} currentPage={currentPage} totalPages={totalPages} />
    </Flex>
  );
};

// ── Dairy page ──────────────────────────────────────────────────────────────

interface PDFExportDairyPageProps extends BasePDFExportProps {
  calculationSummary: DairyFootprintCalculationSummary;
  currentPage: number;
  totalPages: number;
}

const DairyPage = ({ t, title, subtitle, farmName, year, calculationSummary, regionalComparisonValue, currentPage, totalPages }: PDFExportDairyPageProps) => {
  const regionalValueComparisonChange = regionalComparisonValue
    ? ((calculationSummary.overall.perKgYieldCO2e - regionalComparisonValue) / regionalComparisonValue) * 100
    : undefined;

  return (
    <Flex className="break-page footprinting-pdf-export-crop-page" vertical>
      <PageHeader title={title} farmName={farmName} subtitle={subtitle} year={year} />

      <Typography variant="h2" className="footprinting-pdf-export-crop-title">
        {t("pdfExport.dairyOverview")}
      </Typography>

      <Flex className="footprinting-pdf-export-crop-kpi-row" gap={12} wrap="wrap">
        <Flex className="footprinting-pdf-export-kpi-card footprinting-pdf-export-kpi-card-primary" vertical>
          <div className="footprinting-pdf-export-kpi-value footprinting-pdf-export-kpi-value-primary">
            {formatNumber({ number: trimToDecimals(calculationSummary.overall.totalCO2e) })}
          </div>
          <div className="footprinting-pdf-export-kpi-label footprinting-pdf-export-kpi-label-primary">{t("resultsPage.summaryTiles.co2eInTotal")} (kg)</div>
        </Flex>
        <Flex className="footprinting-pdf-export-kpi-card footprinting-pdf-export-kpi-card-neutral" vertical>
          <div className="footprinting-pdf-export-kpi-value footprinting-pdf-export-kpi-value-green">
            {formatNumber({ number: trimToDecimals(calculationSummary.overall.perKgYieldCO2e) })}
          </div>
          <div className="footprinting-pdf-export-kpi-label">{t("resultsPage.summaryTiles.co2ePerKgYield")} (kg)</div>
        </Flex>
        {regionalValueComparisonChange != null ? (
          <Flex className="footprinting-pdf-export-kpi-card footprinting-pdf-export-kpi-card-neutral" vertical>
            <div className="footprinting-pdf-export-kpi-value footprinting-pdf-export-kpi-value-green">
              {formatNumber({ number: trimToDecimals(regionalValueComparisonChange) })}%
            </div>
            <div className="footprinting-pdf-export-kpi-label">
              {regionalComparisonValue} {t("resultsPage.table.columnHeader.perKgYieldCO2e")}
            </div>
          </Flex>
        ) : null}
        <Flex className="footprinting-pdf-export-kpi-card footprinting-pdf-export-kpi-card-neutral" vertical>
          <div className="footprinting-pdf-export-kpi-value footprinting-pdf-export-kpi-value-green">
            {formatNumber({ number: trimToDecimals(calculationSummary.overall.perCowCO2e) })}
          </div>
          <div className="footprinting-pdf-export-kpi-label">{t("resultsPage.summaryTiles.co2ePerCow")} (kg)</div>
        </Flex>
      </Flex>

      <Typography variant="h4" className="footprinting-pdf-export-breakdown-title">
        {t("resultsPage.detailedBreakdownTitle")}
      </Typography>
      {renderDairyCategoryBreakdown(t, calculationSummary)}

      <PageFooter t={t} currentPage={currentPage} totalPages={totalPages} />
    </Flex>
  );
};

export const FootprintingPDFExportPage = {
  CoverPage,
  FarmPage,
  CropPage,
  DairyPage,
};
